import {
  Button,
  DisplayInfo,
  addToast,
  showDialogConfirm,
  showDialogInfo,
} from '@octano/global-ui';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';
import { Col, Row } from 'reactstrap';
import { format as formatRut } from 'rut.js';

import {
  getPDFOfSectionClose,
  getSectionsTeacherPortalByIDRequest,
  postClosedSectionTeacherPortalRequest,
  postRejectClosureSectionMantainerPortalRequest,
  importOnlineAssistance,
} from './api';
import Loading from '../../../components/info/Loading';
import LoadingModal from '../../../components/modals/LoadingModal';
import { PathsLayouts } from '../../../config/routes';
import useUserState from '../../../hooks/useUserState';
import { PermissionName } from '../../../types/Auth';
import DetailBoxPeriodEndReview from './parts/DetailBox';
import TablePeriodEndReview from './parts/Table';
import { GetSectionsTeacherPortalByIDReponse } from '../../../api/requests/MantainerPortal';

export default function NonStructuredPeriodEndReview() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<GetSectionsTeacherPortalByIDReponse>();
  const [btnLoading, setBtnLoading] = useState<boolean>(false);
  const [btnDownloadLoading, setBtnDownloadLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [importingAssistance, setImportingAssistance] = useState<boolean>(
    false,
  );
  const [error, setError] = useState<string | null>();
  const [forbidden, setForbidden] = useState<boolean>(false);
  const history = useHistory();

  const { isAuthorizedTo } = useUserState();
  const isDisabled = !isAuthorizedTo([
    PermissionName.CE_SUBJECT_SECTIONS_PERIOD_CLOSE_CHANGE_STATUS,
  ]);

  const getData = useCallback(async () => {
    setLoading(true);
    const response = await getSectionsTeacherPortalByIDRequest(id);
    if (response.error) {
      // 403: el usuario no tiene permiso (scope) sobre la sección. Diferenciar
      // del error genérico para no mostrar el mensaje engañoso de "falta
      // estructura de evaluación" cuando en realidad es falta de permiso
      // (p. ej. permisos reasignados sin recargar la sesión).
      if (response.error.status === 403) {
        setForbidden(true);
        setError(null);
      } else {
        setForbidden(false);
        setError(response.error.code);
      }
    } else if (response.data) {
      setForbidden(false);
      setError(null);
      setData(response.data);
    }
    setLoading(false);
  }, [id, setData, setLoading]);

  const back = useCallback(() => {
    history.push(`${PathsLayouts.nonStructured}/closing-period`);
  }, [history]);

  const getStudentsPending = useCallback(() => {
    return data?.students.filter((student) => !student.generalPassed) || [];
  }, [data]);

  const hasOnlineAssistancePending = useCallback(() => {
    if (!data?.students || !data?.section) {
      return false;
    }

    const requiredPercentage = data.onlineAssistanceRequirement;

    // Si no hay requerimiento de asistencia online, no está pendiente
    if (
      !requiredPercentage ||
      requiredPercentage.value === null ||
      requiredPercentage.value <= 0
    ) {
      return false;
    }

    // Verificar si algún estudiante tiene asistencia online pendiente
    return data.students.some((student) => {
      const onlineData = student.onlineAssistance;
      return !onlineData;
    });
  }, [data]);

  const postData = useCallback(async () => {
    setBtnLoading(true);
    const resp = await postClosedSectionTeacherPortalRequest(Number(id));
    if (resp.error) {
      addToast({
        color: 'danger',
        icon: 'error',
        text: t(`periodEndReview.addToast.error`),
      });
    } else {
      addToast({
        color: 'success',
        icon: 'success',
        text: t(`periodEndReview.addToast.success`),
      });
      await getData();
    }
    setBtnLoading(false);
  }, [id, t, getData]);

  const downloadPDF = useCallback(async () => {
    setBtnDownloadLoading(true);
    const res = await getPDFOfSectionClose(Number(id));
    console.log({ res });
    if (res.error) {
      addToast({
        color: 'danger',
        icon: 'error',
        text: t(`periodEndReview.addToastDownload.error`),
      });
    } else if (data) {
      addToast({
        color: 'success',
        icon: 'check',
        text: t(`periodEndReview.addToastDownload.success`),
      });
      const name = `ActaDeCierre_${data.section.name}_${dayjs(
        data.section.closedAt,
      ).format('YYYY-MM-DD')}`;
      const a = document.createElement('a');
      document.body.appendChild(a);
      const url = window.URL.createObjectURL(res.data);
      a.href = url;
      a.download = `${name}.${res.data.type.split('/')[1]}`;
      a.click();
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        a.remove();
      }, 0);
    }
    setBtnDownloadLoading(false);
  }, [id, data, t]);

  const rejectToClosed = useCallback(async () => {
    setBtnLoading(true);
    const res = await postRejectClosureSectionMantainerPortalRequest(
      Number(id),
    );
    if (res.error) {
      addToast({
        color: 'danger',
        icon: 'error',
        text: t(`periodEndReview.addToastRejectClosure.error`),
      });
    } else {
      addToast({
        color: 'success',
        icon: 'success',
        text: t(`periodEndReview.addToastRejectClosure.success`),
      });
      history.push(`${PathsLayouts.nonStructured}/closing-period`);
    }
    setBtnLoading(false);
  }, [t, id, history]);

  const handleImportOnlineAssistance = useCallback(async () => {
    if (
      !data?.onlineAssistanceRequirement?.required ||
      data?.onlineAssistanceRequirement?.lastImportedAt
    ) {
      return;
    }
    if (importingAssistance) {
      return;
    }
    setImportingAssistance(true);
    const { error, data: result } = await importOnlineAssistance(id);

    if (!error && result) {
      const importedCount = result.completedStudents;
      // Verificar si no hay estudiantes completados en Moodle
      if (importedCount === 0) {
        showDialogInfo({
          icon: {
            color: 'warning',
            name: 'warning',
          },
          title: t('periodEndReview.importAssistanceModal.notCompleted.title'),
          subtitle: t(
            'periodEndReview.importAssistanceModal.notCompleted.subtitle',
          ),
          btnConfirm: {
            text: t('common.terms.accept'),
            onConfirm: () => {},
          },
        });
        setImportingAssistance(false);
        return;
      }

      addToast({
        color: 'success',
        icon: 'success',
        text: t(`periodEndReview.importAssistanceModal.success`, result),
      });
      getData();
    } else {
      addToast({
        color: 'danger',
        icon: 'error',
        text: t(`periodEndReview.importAssistanceModal.error`),
      });
    }
    setImportingAssistance(false);
  }, [data?.onlineAssistanceRequirement, importingAssistance, id, getData, t]);

  const onSumit = useCallback(async () => {
    // Primero verificar si hay asistencia online pendiente
    if (hasOnlineAssistancePending()) {
      showDialogConfirm({
        icon: {
          color: 'warning',
          name: 'warning',
        },
        title: t('periodEndReview.modalOnlineAssistancePending.title'),
        subtitle: t('periodEndReview.modalOnlineAssistancePending.subtitle'),
        btnClose: {
          text: t('periodEndReview.modalOnlineAssistancePending.btnBack'),
        },
        btnConfirm: {
          text: t(
            'periodEndReview.modalOnlineAssistancePending.btnCloseAnyway',
          ),
          onConfirm: postData,
        },
      });
      return;
    }

    // Si no hay asistencia online pendiente, continuar con el flujo normal
    const studentsPending = getStudentsPending();
    const prefix =
      studentsPending.length === 0
        ? 'notStudentsPending'
        : 'withStudentPending';
    showDialogConfirm({
      icon: {
        color: 'primary',
        name: 'warning',
      },
      title: t(`periodEndReview.modalConfirmToClose.${prefix}.title`),
      subtitle: t(`periodEndReview.modalConfirmToClose.${prefix}.subtitle`),
      children:
        studentsPending.length === 0 ? undefined : (
          <>
            <span>{t('periodEndReview.modalConfirmToClose.label')}</span>
            <ul className="box-ul-detail mb-4">
              {studentsPending.map((student) => (
                <li className="fs-16" key={student.id}>
                  <p>
                    <span>{student.fullName} </span>
                    <span>
                      {student.run
                        ? formatRut(student.run)
                        : student.passportNumber}
                    </span>
                  </p>
                </li>
              ))}
            </ul>
          </>
        ),
      btnClose: { text: t(`common.actions.cancel`) },
      btnConfirm: {
        text: t(`common.actions.confirm`),
        onConfirm: postData,
      },
    });
  }, [postData, t, getStudentsPending, hasOnlineAssistancePending]);

  useEffect(() => {
    getData();
  }, [getData]);

  if (loading) {
    return (
      <div className="mx-3">
        <Loading insideCard />
      </div>
    );
  }

  return (
    <div className="mx-3 py-4 px-3 bg-white rounded">
      <Row>
        <Col>
          <Button
            icon={'back'}
            text={t('common.actions.back')}
            size="sm"
            outlined
            rounded
            onClick={back}
            className={'mb-4'}
          />
        </Col>
        {!error && !forbidden && (
          <Col md={8}>
            <DetailBoxPeriodEndReview
              data={
                data?.section as GetSectionsTeacherPortalByIDReponse['section']
              }
            />
          </Col>
        )}
      </Row>
      <h2 className="text-primary fs-20 fw-700 mt-4 mb-2 text-uppercase">
        {t(`periodEndReview.title`)}
      </h2>
      <p className="fs-16 mt-3 mb-4">{t('periodEndReview.subtitle')}</p>
      {forbidden ? (
        <DisplayInfo
          insideCard
          maxWidth="700px"
          iconName="information"
          title={t('periodEndReview.forbiddenResult.title')}
          textBody={t('periodEndReview.forbiddenResult.subtitle')}
        />
      ) : error ? (
        <DisplayInfo
          insideCard
          maxWidth="700px"
          iconName="information"
          title={t('periodEndReview.errorResult.title')}
          textBody={t('periodEndReview.errorResult.subtitle')}
        />
      ) : (
        <>
          {data?.section.closedAt && (
            <Row className="mb-4">
              <Col md={9} />
              <Col>
                <Button
                  icon="download"
                  text={t('periodEndReview.buttonDownload')}
                  fullwidth
                  outlined
                  onClick={downloadPDF}
                  loading={btnDownloadLoading}
                />
              </Col>
            </Row>
          )}
          <TablePeriodEndReview
            data={
              data?.students as GetSectionsTeacherPortalByIDReponse['students']
            }
            passingGrade={
              data?.passingGrade as GetSectionsTeacherPortalByIDReponse['passingGrade']
            }
            section={
              data?.section as GetSectionsTeacherPortalByIDReponse['section']
            }
            onlineAssistanceRequirement={data?.onlineAssistanceRequirement}
            onPressImportAssistance={
              !data?.section?.closedAt
                ? handleImportOnlineAssistance
                : undefined
            }
          />
        </>
      )}
      <br />
      {data?.section.closedAt && (
        <Row>
          <Col md={9} />
          <Col>
            <Button
              disabled={isDisabled}
              text={t('periodEndReview.bottonRejectClose')}
              fullwidth
              onClick={rejectToClosed}
              loading={btnLoading}
            />
          </Col>
        </Row>
      )}
      {data && !data?.section.closedAt && (
        <Row>
          <Col md={9} />
          <Col>
            <Button
              disabled={isDisabled}
              text={t('periodEndReview.buttonConfirmToClose')}
              fullwidth
              onClick={onSumit}
              loading={btnLoading}
            />
          </Col>
        </Row>
      )}
      <LoadingModal
        title={t('periodEndReview.importAssistanceModal.title')}
        description={t('periodEndReview.importAssistanceModal.description')}
        isOpen={importingAssistance}
      />
    </div>
  );
}
